import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isEmptyWidgetData, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

export class TraderverseStockFinancial {
  private container: HTMLElement;
  private config: FinancialConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentTableData: any = null;
  private currentChart: any;
  private activeMetricsByView: Partial<Record<'incomeStatement' | 'balanceSheet' | 'cashflow', string[]>> = {};
  private widgetResizeObserver: ResizeObserver | null = null;
  private readonly seriesColors = ['#10B981', '#7C3AED', '#F59E0B', '#EF4444', '#3B82F6'];
  /** Distinct left accents per metric row (matches financial statement table styling). */
  private readonly tableAccentColors = [
    '#A855F7', '#1E40AF', '#F97316', '#22C55E', '#EC4899',
    '#38BDF8', '#10B981', '#EF4444', '#EAB308', '#7C3AED'
  ];
  private currentView: 'incomeStatement' | 'balanceSheet' | 'cashflow' = 'incomeStatement';

  constructor(config: FinancialConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<FinancialConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      type: "Cash and Debt",
      theme: 'default-dark',
      width: '100%',
      height: '920px',
      autosize: true,
      period: 'Yearly',
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}



    const typeOptions = [
      "Cash and Debt",
      "Assets and StockHolders",
      "Outstanding Shares & Buyback",
      "Revenue and Income",
      "Cash Flow Data"
    ].map(s => `<option value="${s}" ${this.config.type === s ? 'selected' : ''}>${s}</option>`).join('');

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()}Financial Summary</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <span></span>
            <div class="item tabsDv">
              <button class="financial-view-btn active" data-view="incomeStatement">Income Statement</button>
              <button class="financial-view-btn" data-view="balanceSheet">Balance Sheet</button>
              <button class="financial-view-btn" data-view="cashflow">Cashflow</button>
            </div>
            <span></span>
            <div class="item tabsDv">
              <button class="financial-period-btn ${this.config.period === 'Yearly' ? 'active' : ''}" data-period="Yearly">Yearly</button>
              <button class="financial-period-btn ${this.config.period === 'Quarterly' ? 'active' : ''}" data-period="Quarterly">Quarterly</button>
            </div>
            <div class="filtersBox sideBar hidden">
              <div class="item">
                <select class="type-dropdown dropdown-btn">${typeOptions}</select>
              </div>
              <div class="item">
                <select class="period-dropdown dropdown-btn">
                  <option value="Yearly" ${this.config.period === 'Yearly' ? 'selected' : ''}>Yearly</option>
                  <option value="Quarterly" ${this.config.period === 'Quarterly' ? 'selected' : ''}>Quarterly</option>
                </select>
              </div>
            </div>
          </div>
          <div class="chartDv">
            <div class="financial-chart-stack">
              <div id="cashDebtContainer" class="chart"></div>
              <div id="financialDataTable" class="financial-data-table-wrap"></div>
            </div>
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }
  }


  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;

    const typeDropdown = this.shadowRoot.querySelector('.type-dropdown') as HTMLSelectElement;
    typeDropdown?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.type = value;
      this.emit('typeChange', { type: value });
      this.updateDisplay();
    });

    const viewButtons = this.shadowRoot.querySelectorAll('.financial-view-btn');
    viewButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const view = (button as HTMLElement).dataset.view as typeof this.currentView | undefined;
        if (!view || view === this.currentView) return;
        this.currentView = view;
        this.updateDisplay();
      });
    });

    const periodButtons = this.shadowRoot.querySelectorAll('.financial-period-btn');
    periodButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const period = (button as HTMLElement).dataset.period as 'Yearly' | 'Quarterly' | undefined;
        if (!period || this.config.period === period) return;
        this.config.period = period;
        this.updateDisplay();
      });
    });

    const periodDropdown = this.shadowRoot.querySelector('.period-dropdown') as HTMLSelectElement;
    periodDropdown?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.period = value;
      this.updateDisplay();
    });

    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', () => {
        filtersBox.classList.toggle('hidden');
      });
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
        try {
            const response = await apiGet({
                method: "POST",
                url: `${APIURL}/v2/financial/summary/charts?symbol=${symbol}`,
                body: { symbol },
            });

            if (!response.ok) throw new Error("Failed to fetch data");
            const json = await response.json();
            this.currentData = json?.data || {};
            this.currentTableData = await this.fetchFinancialTableData(symbol);
            if (!this.currentData[this.config.type!]) {
              this.config.type = Object.keys(this.currentData)[0];
            }
const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
if (chartDv && this.currentData && Object.keys(this.currentData).length > 0) {
  chartDv.setAttribute('data-testing-id', `financial-chart-${symbol}`);
}
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } catch (err) {
            console.error("Error fetching chart data:", err);
        }
    }

  private async fetchFinancialTableData(symbol: string): Promise<any> {
    try {
      const [yearly, quarterly] = await Promise.all([
        this.fetchFinancialDetailsPeriod(symbol, true),
        this.fetchFinancialDetailsPeriod(symbol, false),
      ]);
      return {
        'Revenue and Income': { Yearly: yearly.income, Quarterly: quarterly.income },
        'Assets and StockHolders': { Yearly: yearly.balance, Quarterly: quarterly.balance },
        'Cash Flow Data': { Yearly: yearly.cashflow, Quarterly: quarterly.cashflow },
        // Keep legacy tabs mapped to the closest available details sections.
        'Cash and Debt': { Yearly: yearly.balance, Quarterly: quarterly.balance },
        'Outstanding Shares & Buyback': { Yearly: yearly.cashflow, Quarterly: quarterly.cashflow },
      };
    } catch (error) {
      console.error('Error fetching table data from financial details:', error);
      return null;
    }
  }

  private async fetchFinancialDetailsPeriod(symbol: string, isYearly: boolean): Promise<{
    income: Record<string, [number, number | null][]>;
    balance: Record<string, [number, number | null][]>;
    cashflow: Record<string, [number, number | null][]>;
  }> {
    const res = await apiGet({
      url: `${APIURL}/v2/financial/details?symbol=${symbol}&is_yearly=${isYearly}`,
    });
    if (!res.ok) throw new Error(`Failed to fetch financial details (${res.status})`);
    const json = await res.json();
    const payload = json?.data || {};
    return {
      income: this.normalizeFinancialDetailsSection(payload.financials_income_statement_fmp?.data),
      balance: this.normalizeFinancialDetailsSection(payload.financials_balance_sheet_fmp?.data),
      cashflow: this.normalizeFinancialDetailsSection(payload.financials_cash_flow_fmp?.data),
    };
  }

  private normalizeFinancialDetailsSection(
    section: Record<string, Record<string, [unknown, unknown]>> | undefined
  ): Record<string, [number, number | null][]> {
    if (!section) return {};

    const dateKeys = Object.keys(section)
      .filter((date) => Number.isFinite(Date.parse(date)))
      .sort((a, b) => Date.parse(a) - Date.parse(b));
    if (!dateKeys.length) return {};

    const metricSet = new Set<string>();
    for (const dateKey of dateKeys) {
      const row = section[dateKey] || {};
      for (const [metric, metricPair] of Object.entries(row)) {
        const value = Array.isArray(metricPair) ? metricPair[0] : null;
        if (typeof value === 'number' && Number.isFinite(value)) metricSet.add(metric);
      }
    }
    if (!metricSet.size) return {};

    const normalized: Record<string, [number, number | null][]> = {};
    const percentageSeries: Record<string, [number, number | null][]> = {};
    for (const metric of metricSet) {
      normalized[metric] = [];
      percentageSeries[`${metric} percentage`] = [];
    }

    for (const dateKey of dateKeys) {
      const timestamp = Date.parse(dateKey);
      const row = section[dateKey] || {};
      for (const metric of metricSet) {
        const metricPair = row[metric];
        const rawValue = Array.isArray(metricPair) ? metricPair[0] : null;
        const rawPct = Array.isArray(metricPair) ? metricPair[1] : null;
        const value = typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : null;
        const pct = typeof rawPct === 'number' && Number.isFinite(rawPct) ? rawPct : null;
        normalized[metric].push([timestamp, value]);
        percentageSeries[`${metric} percentage`].push([timestamp, pct]);
      }
    }

    Object.entries(percentageSeries).forEach(([metric, points]) => {
      const hasAnyValue = points.some(([, value]) => typeof value === 'number' && Number.isFinite(value));
      if (hasAnyValue) normalized[metric] = points;
    });

    return normalized;
  }

  private updateDisplay(): void {
    const container = this.shadowRoot.querySelector('#cashDebtContainer') as HTMLDivElement | null;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (isEmptyWidgetData(this.currentData)) {
      if (container) container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.syncViewUi();
    this.updateLegends();
    this.renderSingleSeriesView(this.currentView);
    this.renderFinancialTable();
  }

  private renderChartByType(type: string): void {
    const container = this.shadowRoot.querySelector('#cashDebtContainer') as HTMLDivElement | null;
    if (!container) return;
    container.innerHTML = '';
    this.drawChartByType(type);
  }

  private getCurrentFinancialType(): string {
    if (this.currentView === 'incomeStatement') return 'Revenue and Income';
    if (this.currentView === 'balanceSheet') return 'Assets and StockHolders';
    return 'Cash Flow Data';
  }

  private getSelectedPeriodData(typeOverride?: string): Record<string, [number, number | null][]> | null {
    const activeType = typeOverride || this.getCurrentFinancialType();
    if (!this.currentData || !activeType || !this.config.period) return null;
    const section = this.currentData[activeType];
    if (!section || !section[this.config.period]) return null;
    return section[this.config.period];
  }

  private getSelectedTablePeriodData(typeOverride?: string): Record<string, [number, number | null][]> | null {
    const activeType = typeOverride || this.getCurrentFinancialType();
    if (!this.currentTableData || !activeType || !this.config.period) return null;
    const section = this.currentTableData[activeType];
    if (!section || !section[this.config.period]) return null;
    return section[this.config.period];
  }

  private limitTablePeriods(
    data: Record<string, [number, number | null][]>
  ): Record<string, [number, number | null][]> {
    const maxPeriods = this.config.period === 'Quarterly' ? 8 : 12;
    const firstKey = Object.keys(data)[0];
    const total = firstKey ? (data[firstKey] || []).length : 0;
    if (total <= maxPeriods) return data;

    const start = Math.max(0, total - maxPeriods);
    const sliced: Record<string, [number, number | null][]> = {};
    Object.entries(data).forEach(([k, points]) => {
      sliced[k] = (points || []).slice(start);
    });
    return sliced;
  }

  private syncViewUi(): void {
    const buttons = this.shadowRoot.querySelectorAll('.financial-view-btn');
    buttons.forEach((button) => {
      const isActive = (button as HTMLElement).dataset.view === this.currentView;
      button.classList.toggle('active', isActive);
    });
    const periodButtons = this.shadowRoot.querySelectorAll('.financial-period-btn');
    periodButtons.forEach((button) => {
      const isActive = (button as HTMLElement).dataset.period === this.config.period;
      button.classList.toggle('active', isActive);
    });
  }

  private getSingleSeriesConfig(view: 'incomeStatement' | 'balanceSheet' | 'cashflow'): {
    color: string;
    preferredKeys: string[];
  } {
    if (view === 'incomeStatement') {
      return { color: '#FF4D4F', preferredKeys: ['revenue', 'total revenue', 'net income', 'income'] };
    }
    if (view === 'balanceSheet') {
      return { color: '#14F1E1', preferredKeys: ['total assets', 'assets', 'total equity', 'equity'] };
    }
    return { color: '#16C63A', preferredKeys: ['operating cash flow', 'free cash flow', 'cash flow', 'cash from operations'] };
  }

  private pickPrimarySeriesKey(
    selectedPeriodData: Record<string, [number, number | null][]>,
    preferredKeys: string[]
  ): string | null {
    const seriesKeys = Object.keys(selectedPeriodData);
    const nonPercentageKeys = seriesKeys.filter((key) => !key.toLowerCase().includes('percentage'));

    for (const preferred of preferredKeys) {
      const match = nonPercentageKeys.find((key) => key.toLowerCase().includes(preferred));
      if (match) return match;
    }

    return nonPercentageKeys[0] || seriesKeys[0] || null;
  }

  private renderSingleSeriesView(view: 'incomeStatement' | 'balanceSheet' | 'cashflow'): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#cashDebtContainer') as HTMLDivElement | null;
    if (!container) return;

    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const selectedPeriodData = this.getSelectedTablePeriodData(this.getCurrentFinancialType())
      || this.getSelectedPeriodData(this.getCurrentFinancialType());
    if (!selectedPeriodData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const seriesConfig = this.getSingleSeriesConfig(view);
    const selectedMetricKeys = this.getActiveMetricsForView(view, selectedPeriodData, seriesConfig.preferredKeys);
    if (!selectedMetricKeys.length) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const points = selectedPeriodData[selectedMetricKeys[0]] || [];
    if (!points.length) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const xAxisData = points.map((item: [number, number | null]) => {
      const date = new Date(item[0]);
      return this.config.period === 'Yearly'
        ? `${date.getUTCFullYear()}`
        : `${date.toLocaleString('default', { month: 'short' })} ${date.getUTCFullYear()}`;
    });
    const metricKeys = Object.keys(selectedPeriodData).filter((key) => !key.toLowerCase().includes('percentage'));
    const series = selectedMetricKeys.map((seriesKey) => {
      const yAxisData = (selectedPeriodData[seriesKey] || []).map((item: [number, number | null]) => item[1]);
      const colorIndex = Math.max(0, metricKeys.indexOf(seriesKey));
      const lineColor = this.seriesColors[colorIndex % this.seriesColors.length] || seriesConfig.color;
      return {
        name: this.formatMetricLabel(seriesKey),
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: yAxisData,
      
        lineStyle: {
          width: 2,
          color: lineColor
        },
      
        itemStyle: {
          color: lineColor
        },
      
        areaStyle: {
          opacity: 0.18,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: lineColor
            },
            {
              offset: 1,
              color: 'rgba(255,255,255,0)'
            }
          ])
        }
      };
    });

    const theme = this.getChartThemeVars(widgetBox);

    const chart = echarts.init(container, null, { backgroundColor: theme.bgPrimary });
    chart.setOption({
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 0, right: 0, top: 18, bottom: 0, containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.bgColor,
        borderColor: theme.axisLineColor,
        textStyle: { color: theme.textColor, fontSize: theme.fontSize },
        valueFormatter: (val: number | null) => val === null ? 'N/A' : this.formatAxisLabel(val)
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        boundaryGap: false,
        axisLabel: { color: theme.textColor, fontSize: theme.fontSize, margin: 16 },
        axisLine: { lineStyle: { color: theme.axisLineColor } },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: { color: theme.textColor, fontSize: theme.fontSize, formatter: (v: number) => this.formatAxisLabel(v) },
        splitLine: { show: true, lineStyle: { color: theme.splitLineColor, type: 'dashed' } }
      },
      series
    });

    this.currentChart = chart;
    this.scheduleChartResize();
  }

  private getActiveMetricsForView(
    view: 'incomeStatement' | 'balanceSheet' | 'cashflow',
    selectedPeriodData: Record<string, [number, number | null][]>,
    preferredKeys: string[]
  ): string[] {
    const metricKeys = Object.keys(selectedPeriodData).filter((key) => !key.toLowerCase().includes('percentage'));
    const selected = (this.activeMetricsByView[view] || []).filter((key) => metricKeys.includes(key));
    if (selected.length) {
      this.activeMetricsByView[view] = selected;
      return selected;
    }
    const fallback = this.pickPrimarySeriesKey(selectedPeriodData, preferredKeys);
    const defaults = fallback ? [fallback] : [];
    this.activeMetricsByView[view] = defaults;
    return defaults;
  }

  /** Theme tokens from `.widgetBox` CSS vars — aligned with IPO Price Distribution and other charts. */
  private getChartThemeVars(widgetBox: HTMLElement): {
    textColor: string;
    axisLineColor: string;
    splitLineColor: string;
    fontSize: string;
    bgColor: string;
    bgPrimary: string;
  } {
    const cs = getComputedStyle(widgetBox);
    return {
      textColor: cs.getPropertyValue('--charts-text-color').trim(),
      axisLineColor: cs.getPropertyValue('--charts-axis-color').trim(),
      splitLineColor: cs.getPropertyValue('--charts-split-line-color').trim(),
      fontSize: cs.getPropertyValue('--font-size-sm').trim(),
      bgColor: cs.getPropertyValue('--base-dark').trim(),
      bgPrimary: cs.getPropertyValue('--bg-primary').trim(),
    };
  }

  /** ECharts needs a laid-out DOM size; resize after flex/grid assigns height */
  private scheduleChartResize(): void {
    const inst = this.currentChart;
    if (!inst || typeof inst.resize !== 'function') return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          inst.resize();
        } catch {
          /* ignore */
        }
      });
    });
  }

  private formatAxisLabel(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1e12) return `${(value / 1e12).toFixed(1)}T`;
    if (abs >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (abs >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return `${value}`;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** Turn API keys like `netIncome` / `total_revenue` into readable labels. */
  private formatMetricLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
  }

  private formatPeriodHeader(ts: number): string {
    const d = new Date(ts);
    if (this.config.period === 'Quarterly') {
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  }

  private formatPrimaryCellValue(metricKey: string, v: number | null): string {
    if (v === null || v === undefined || Number.isNaN(v as number)) return 'null';
    const kl = metricKey.toLowerCase();
    if ((kl.includes('date') || kl.includes('time') || kl.includes('accepted')) && v > 1e11) {
      const d = new Date(v);
      if (kl.includes('accepted') || (kl.includes('time') && !kl.includes('date'))) {
        return d.toISOString().replace(/\.\d{3}Z$/, '');
      }
      return d.toISOString().slice(0, 10);
    }
    const abs = Math.abs(v);
    if (abs >= 1e9 || (abs >= 1000 && Number.isFinite(v) && Math.abs(v - Math.round(v)) < 1e-6)) {
      return Math.round(v).toLocaleString('en-US');
    }
    if (Number.isInteger(v)) return String(v);
    return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }

  private findPercentageSeriesKey(metricKey: string, allKeys: string[]): string | null {
    const m = metricKey.toLowerCase().trim();
    for (const k of allKeys) {
      if (!k.toLowerCase().includes('percentage')) continue;
      const stripped = k.toLowerCase().replace(/\s*percentage\s*/gi, '').trim();
      if (stripped === m || m.includes(stripped) || stripped.includes(m)) return k;
    }
    const normalized = m.replace(/[^a-z0-9]/gi, '');
    for (const k of allKeys) {
      if (!k.toLowerCase().includes('percentage')) continue;
      const ks = k.toLowerCase().replace(/[^a-z0-9]/gi, '');
      if (ks.includes(normalized) || normalized.includes(ks.replace(/percentage/g, ''))) return k;
    }
    return null;
  }

  private computeYoYGrowth(values: (number | null)[]): (number | null)[] {
    return values.map((val, i) => {
      if (i === 0 || val === null || val === undefined) return null;
      const prev = values[i - 1];
      if (prev === null || prev === undefined || prev === 0) return null;
      return ((val - prev) / Math.abs(prev)) * 100;
    });
  }

  private growthCellMeta(value: number): { text: string; kind: 'pos' | 'neg' | 'zero' } {
    if (Math.abs(value) < 1e-9) return { text: '0%', kind: 'zero' };
    const rounded = Math.abs(value) >= 10 ? value.toFixed(1) : value.toFixed(2);
    const text = `${rounded}%`;
    return { text, kind: value > 0 ? 'pos' : 'neg' };
  }

  private renderFinancialTable(): void {
    const wrap = this.shadowRoot.querySelector('#financialDataTable') as HTMLElement | null;
    if (!wrap) return;

    const rawPeriodData = this.getSelectedTablePeriodData() || this.getSelectedPeriodData();
    const selectedPeriodData = rawPeriodData ? this.limitTablePeriods(rawPeriodData) : null;
    if (!selectedPeriodData) {
      wrap.innerHTML = '';
      return;
    }

    const seriesKeys = Object.keys(selectedPeriodData);
    const anchorSeries = selectedPeriodData[seriesKeys[0]] || [];
    if (!anchorSeries.length) {
      wrap.innerHTML = '';
      return;
    }

    const metricKeys = seriesKeys.filter((key) => !key.toLowerCase().includes('percentage'));
    if (!metricKeys.length) {
      wrap.innerHTML = '';
      return;
    }

    const seriesConfig = this.getSingleSeriesConfig(this.currentView);
    const activeMetricKeys = this.getActiveMetricsForView(this.currentView, selectedPeriodData, seriesConfig.preferredKeys);
    this.fillFinancialTableEl(wrap, selectedPeriodData, metricKeys, anchorSeries, new Set(activeMetricKeys));
  }

  private fillFinancialTableEl(
    wrap: HTMLElement,
    selectedPeriodData: Record<string, [number, number | null][]>,
    metricKeys: string[],
    anchorSeries: [number, number | null][],
    activeMetricKeys: Set<string> = new Set()
  ): void {
    const allKeys = Object.keys(selectedPeriodData);
    const periodCount = anchorSeries.length;

    const headerCells = anchorSeries
      .map(([ts]) => `<th class="financial-th-period">${this.escapeHtml(this.formatPeriodHeader(ts))}</th>`)
      .join('');

    const periodColTags = anchorSeries.map(() => '<col class="financial-col-period" />').join('');

    const rowsHtml = metricKeys.map((metricKey, rowIdx) => {
      const points = selectedPeriodData[metricKey] || [];
      const values = Array.from({ length: periodCount }, (_, i) => points[i]?.[1] ?? null);
      const pctKey = this.findPercentageSeriesKey(metricKey, allKeys);
      const pctPoints = pctKey ? selectedPeriodData[pctKey] || [] : [];
      const pctValues = pctPoints.length
        ? Array.from({ length: periodCount }, (_, i) => pctPoints[i]?.[1] ?? null)
        : [];
      const computedGrowth = this.computeYoYGrowth(values);
      const accent = this.tableAccentColors[rowIdx % this.tableAccentColors.length];
      const isActive = activeMetricKeys.has(metricKey);

      const primaryTds = values
        .map((v) => `<td class="financial-td-val">${this.escapeHtml(this.formatPrimaryCellValue(metricKey, v))}</td>`)
        .join('');

      const growthTds = values.map((_, colIdx) => {
        let g: number | null = null;

        if (pctKey && pctPoints.length) {
          const pv = pctValues[colIdx];
          if (pv !== null && pv !== undefined && !Number.isNaN(pv as number)) g = pv as number;
        } else {
          g = computedGrowth[colIdx];
        }

        if (g === null || g === undefined || Number.isNaN(g)) {
          return `<td class="financial-td-growth financial-growth-null">null%</td>`;
        }

        const meta = this.growthCellMeta(g);
        const cls =
          meta.kind === 'pos'
            ? 'financial-growth-pos'
            : meta.kind === 'neg'
              ? 'financial-growth-neg'
              : 'financial-growth-zero';
        return `<td class="financial-td-growth ${cls}">${this.escapeHtml(meta.text)}</td>`;
      }).join('');

      return `
        <tbody class="financial-metric-group financial-metric-selectable ${isActive ? 'financial-metric-active' : ''}" data-metric-key="${this.escapeHtml(metricKey)}">
        <tr class="financial-row-primary" data-metric-key="${this.escapeHtml(metricKey)}">
          <td rowspan="2" class="financial-td-accent"><span class="financial-accent-bar" style="background:${accent};"></span></td>
          <td class="financial-td-metric" style="cursor:pointer;${isActive ? 'font-weight:600;' : ''}">${this.escapeHtml(this.formatMetricLabel(metricKey))}</td>
          ${primaryTds}
        </tr>
        <tr class="financial-row-growth" data-metric-key="${this.escapeHtml(metricKey)}" style="${isActive ? 'background:rgba(124,58,237,0.08);' : ''}">
          <td class="financial-td-growth-label">Growth YoY</td>
          ${growthTds}
        </tr>
        </tbody>`;
    }).join('');

    const tableInlineStyle = this.config.period === 'Quarterly' ? ' style="width:1600px;"' : '';

    wrap.innerHTML = `
      <table class="financial-data-table"${tableInlineStyle} cellspacing="0" cellpadding="0">
        <colgroup>
          <col class="financial-col-accent" />
          <col class="financial-col-label" />
          ${periodColTags}
        </colgroup>
        <thead>
          <tr class="financial-thead-row">
            <th colspan="2" class="financial-th-values-label">Values in USD</th>
            ${headerCells}
          </tr>
        </thead>
        ${rowsHtml}
      </table>`;
    this.attachTableMetricClickHandlers();
  }

  private attachTableMetricClickHandlers(): void {
    const rows = this.shadowRoot.querySelectorAll('#financialDataTable .financial-metric-selectable');
    rows.forEach((row) => {
      row.addEventListener('click', () => {
        if (this.currentView === 'incomeStatement' || this.currentView === 'balanceSheet' || this.currentView === 'cashflow') {
          const metricKey = (row as HTMLElement).dataset.metricKey;
          if (!metricKey) return;
          const selectedSet = new Set(this.activeMetricsByView[this.currentView] || []);
          if (selectedSet.has(metricKey)) {
            if (selectedSet.size > 1) selectedSet.delete(metricKey);
          } else {
            selectedSet.add(metricKey);
          }
          this.activeMetricsByView[this.currentView] = Array.from(selectedSet);
          this.renderSingleSeriesView(this.currentView);
          this.renderFinancialTable();
        }
      });
    });
  }

  private drawChartByType(type: string): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#cashDebtContainer') as HTMLDivElement;
    if (!container) return;
    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const selectedPeriodData = this.getSelectedPeriodData(type);
    if (!selectedPeriodData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const seriesKeys = Object.keys(selectedPeriodData);
    if (!seriesKeys.length) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const anchorSeries = selectedPeriodData[seriesKeys[0]] || [];
    const xAxisData = anchorSeries.map((item: [number, number | null]) => {
      const date = new Date(item[0]);
      return this.config.period === 'Yearly'
        ? `${date.getUTCFullYear()}`
        : `${date.toLocaleString('default', { month: 'short' })} ${date.getUTCFullYear()}`;
    });

    const visibleKeys = seriesKeys;

    const totalBars = Math.max(1, visibleKeys.filter((key) => !key.toLowerCase().includes('percentage')).length);
    const barWidthPercent = `${Math.max(8, Math.floor(80 / totalBars))}%`;

    const seriesData = seriesKeys.map((key, index) => {
      const isPercentage = key.toLowerCase().includes('percentage');
      const values = (selectedPeriodData[key] || []).map((item: [number, number | null]) => item[1]);
      return {
        name: this.formatMetricLabel(key),
        type: isPercentage ? 'line' : 'bar',
        yAxisIndex: isPercentage ? 1 : 0,
        data: values,
        barWidth: isPercentage ? undefined : barWidthPercent,
        smooth: isPercentage,
        itemStyle: { color: this.seriesColors[index % this.seriesColors.length], borderRadius: 0 },
        lineStyle: isPercentage ? { width: 2, color: this.seriesColors[index % this.seriesColors.length] } : undefined
      };
    });

    const theme = this.getChartThemeVars(widgetBox);

    const option = {
      backgroundColor: 'transparent',
      animation: false,
      grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
      tooltip: {
        trigger: 'axis',
        backgroundColor: theme.bgColor,
        textStyle: { color: theme.textColor, fontSize: theme.fontSize },
        valueFormatter: (val: number | null) => val === null ? 'N/A' : this.formatAxisLabel(val)
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLabel: { color: theme.textColor, fontSize: theme.fontSize, margin: 16 },
        axisLine: { lineStyle: { color: theme.axisLineColor } }
      },
      yAxis: [
        {
          type: 'value',
          axisLabel: { color: theme.textColor, fontSize: theme.fontSize, formatter: (v: number) => this.formatAxisLabel(v) },
          splitLine: { show: true, lineStyle: { color: theme.splitLineColor, type: 'dashed' } }
        },
        {
          type: 'value',
          axisLabel: { color: theme.textColor, fontSize: theme.fontSize, formatter: (v: number) => `${v}%` },
          splitLine: { show: false }
        }
      ],
      series: seriesData
    };

    const chart = echarts.init(container, null, { backgroundColor: theme.bgPrimary });
    chart.setOption(option);
    this.currentChart = chart;
    this.scheduleChartResize();
  }

  private updateLegends(): void {
    const legendContainer = this.shadowRoot.querySelector('.legendsCollection');
    if (!legendContainer) return;

    const singleSeriesConfig = this.getSingleSeriesConfig(this.currentView);
    legendContainer.innerHTML = `<span class="series-indicator" style="background-color:${singleSeriesConfig.color};"></span>`;
  }



  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseStockFinancial: typeof TraderverseStockFinancial; } }
if (typeof window !== 'undefined') { window.TraderverseStockFinancial = TraderverseStockFinancial; }
